import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { useQueryStable } from '../useQueryStable';
import { GetAppliedNode, GetDefinitionNode } from './testUtil';

type Result = ReturnType<typeof useSourceByEnvironment>[0];

export type SourceResponse = NonNullable<Result['data']>;

export type RequestedSourceAppliedNode = GetAppliedNode<'sources', SourceResponse>;

export type RequestedSourceDefinitionNode = GetDefinitionNode<
  'sources',
  SourceResponse
>;

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetSourceByUniqueId = graphql(/* GraphQL */ `
  query Source($environmentId: BigInt!, $uniqueIds: [String!], $userId: BigInt) {
    environment(id: $environmentId, userId: $userId) {
      adapterType
      applied {
        lastUpdatedAt
        latestGitSha
        sources(first: 1, filter: { uniqueIds: $uniqueIds }) {
          edges {
            node {
              fqn
              name
              uniqueId
              tags
              description
              meta
              database
              schema
              identifier
              loader
              sourceDescription
              sourceName
              filePath
              patchPath
              projectId
              healthIssues
              children {
                uniqueId
              }
              catalog {
                type
                owner
                comment
                columns {
                  type
                  name
                  comment
                  tags
                  description
                  descriptionOriginColumnName
                  descriptionOriginResourceUniqueId
                  meta
                }
                primaryKey
                rowCountStat
                bytesStat
                stats {
                  id
                  label
                  value
                  description
                  include
                }
              }
              freshness {
                freshnessRunId
                freshnessStatus
                freshnessChecked
                freshnessRunGeneratedAt
                maxLoadedAt
                snapshottedAt
                criteria {
                  errorAfter {
                    count
                    period
                  }
                  warnAfter {
                    count
                    period
                  }
                }
              }
              tests {
                uniqueId
                name
                columnName
                executionInfo {
                  lastRunStatus
                  lastRunError
                }
                testMetadata {
                  name
                  kwargs
                }
              }
            }
          }
        }
      }
    }
  }
`);

type SourceByEnvironmentParams = {
  environmentId: number | undefined;
  userId: number | 'loading' | null;
  uniqueId: string;
  pause?: boolean;
};

export const useSourceByEnvironment = ({
  environmentId = 0,
  userId,
  uniqueId,
  pause,
}: SourceByEnvironmentParams) => {
  const query = useQueryStable({
    query: GetSourceByUniqueId,
    variables: {
      environmentId,
      uniqueIds: [uniqueId],
      userId: userId as number | null,
    },
    pause: pause || !environmentId || !uniqueId || userId === 'loading',
  });

  useCacheMonitor({
    applied: query[0].data?.environment.applied?.lastUpdatedAt,
  });

  return query;
};
